import React, { useContext } from "react";
import "./CombinedTimeline.scss";
import { Fade } from "react-reveal";
import StyleContext from "../../contexts/StyleContext";
import Timeline from "../../components/timeline/Timeline";
import { workExperiences, educationInfo } from "../../portfolio";
import awsLogo from "../../assets/images/awslogoo.png";
import databricksLogo from "../../assets/images/databricks.png";
import awsCommunityLogo from "../../assets/images/awscommunitybuilder.png";

export default function CombinedTimeline() {
  const { isDark } = useContext(StyleContext);

  if (!workExperiences.display && !educationInfo.display) {
    return null;
  }

  // Transform experience data for Timeline component
  const experienceItems = workExperiences.experience.map(exp => ({
    title: exp.company,
    subtitle: exp.role,
    date: exp.date,
    logo: exp.companylogo,
    description: exp.desc,
    bullets: exp.descBullets,
    projects: exp.projects,
    type: 'experience',
    startDate: exp.startDate || exp.date // Fallback to date if startDate not provided
  }));

  // Transform education data for Timeline component
  const educationItems = educationInfo.schools.map(school => ({
    title: school.schoolName,
    subtitle: school.subHeader,
    date: school.duration,
    logo: school.logo,
    description: school.desc,
    bullets: school.descBullets,
    projects: school.projects,
    tags: school.coursework,
    type: 'education',
    startDate: school.startDate || school.duration // Fallback to duration if startDate not provided
  }));

  return (
    <>
      <section className={`section ${isDark ? "dark-mode" : ""}`} id="experience-education">
        <div className="container">
          <Fade bottom duration={1000} distance="20px">
            <p className="typography-eyebrow mb-2">My Journey</p>

            <div className="grid grid-2">
              <div className="timeline-column">
                <h3 className="timeline-column-heading">Work Experience</h3>
                <Timeline items={experienceItems} />
              </div>

              <div className="timeline-column">
                <h3 className="timeline-column-heading">Academic Background</h3>
                <Timeline items={educationItems} />
              </div>
            </div>
          </Fade>
        </div>
      </section>

      <section className={`section ${isDark ? "dark-mode" : ""}`} id="technical-skills">
      <div className="container">
        <Fade bottom duration={1000} distance="20px">
          <p className="typography-eyebrow mb-2">My Expertise</p>
          <h2 className="section-title mb-3">Technical Skills</h2>

          <div className="ruchi-tech-stack-grid">
            {/* Programming Languages */}
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" alt="Python" className="ruchi-tech-logo" />
              <p>Python</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" alt="JavaScript" className="ruchi-tech-logo" />
              <p>JavaScript</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" className="ruchi-tech-logo" />
              <p>Java</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bash/bash-original.svg" alt="Bash" className="ruchi-tech-logo" />
              <p>Bash</p>
            </div>

            {/* Databases */}
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" alt="SQL" className="ruchi-tech-logo" />
              <p>SQL</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" alt="PostgreSQL" className="ruchi-tech-logo" />
              <p>PostgreSQL</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" alt="MongoDB" className="ruchi-tech-logo" />
              <p>MongoDB</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://www.vectorlogo.zone/logos/snowflake/snowflake-icon.svg" alt="Snowflake" className="ruchi-tech-logo" />
              <p>Snowflake</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg" alt="Redis" className="ruchi-tech-logo" />
              <p>Redis</p>
            </div>

            {/* Data Engineering */}
            <div className="ruchi-tech-item">
              <img src="https://static-00.iconduck.com/assets.00/airflow-icon-512x512-tpr318yf.png" alt="Apache Airflow" className="ruchi-tech-logo" />
              <p>Apache Airflow</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://upload.wikimedia.org/wikipedia/commons/0/05/Apache_kafka.svg" alt="Apache Kafka" className="ruchi-tech-logo" />
              <p>Apache Kafka</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://seeklogo.com/images/D/dbt-logo-500AB0BAA7-seeklogo.com.png" alt="dbt" className="ruchi-tech-logo" />
              <p>dbt</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://upload.wikimedia.org/wikipedia/commons/f/f3/Apache_Spark_logo.svg" alt="Apache Spark" className="ruchi-tech-logo" />
              <p>Apache Spark</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.worldvectorlogo.com/logos/hadoop.svg" alt="Hadoop" className="ruchi-tech-logo" />
              <p>Hadoop</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="/images/databricks.png" alt="Databricks" className="ruchi-tech-logo" />
              <p>Databricks</p>
            </div>

            {/* Cloud & DevOps */}
            <div className="ruchi-tech-item">
              <img src="/images/awslogoo.png" alt="AWS" className="ruchi-tech-logo" />
              <p>AWS</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Azure" className="ruchi-tech-logo" />
              <p>Azure</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" alt="Docker" className="ruchi-tech-logo" />
              <p>Docker</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" className="ruchi-tech-logo" />
              <p>Kubernetes</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg" alt="Terraform" className="ruchi-tech-logo" />
              <p>Terraform</p>
            </div>

            {/* BI & Analytics */}
            <div className="ruchi-tech-item">
              <img src="https://cdn.worldvectorlogo.com/logos/tableau-software.svg" alt="Tableau" className="ruchi-tech-logo" />
              <p>Tableau</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://raw.githubusercontent.com/microsoft/PowerBI-Icons/main/SVG/Power-BI.svg" alt="Power BI" className="ruchi-tech-logo" />
              <p>Power BI</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.worldvectorlogo.com/logos/looker.svg" alt="Looker" className="ruchi-tech-logo" />
              <p>Looker</p>
            </div>
            <div className="ruchi-tech-item">
              <img src="https://cdn.worldvectorlogo.com/logos/mixpanel.svg" alt="Mixpanel" className="ruchi-tech-logo" />
              <p>Mixpanel</p>
            </div>
          </div>
        </Fade>
      </div>
    </section>

    <section className={`section ${isDark ? "dark-mode" : ""}`} id="certifications">
      <div className="container">
        <Fade bottom duration={1000} distance="20px">
          <p className="typography-eyebrow mb-2">My Credentials</p>
          <h2 className="section-title mb-3">Certifications & Recognitions</h2>

          <div className="ruchi-certifications-items">
            <div className="ruchi-certification-item">
              <div className="ruchi-certification-logo">
                <img src={awsLogo} alt="AWS" />
              </div>
              <div className="ruchi-certification-details">
                <div className="ruchi-certification-header">
                  <h4>AWS Cloud Architect</h4>
                </div>
                <a href="https://www.credly.com/badges/5e3ed7e4-4dc6-4565-ba1f-fc1416cd1989/linked_in_profile" target="_blank" rel="noopener noreferrer" className="ruchi-credential-link">
                  <i className="fas fa-external-link-alt"></i> View Credential
                </a>
              </div>
            </div>

            <div className="ruchi-certification-item">
              <div className="ruchi-certification-logo">
                <img src={awsLogo} alt="AWS" />
              </div>
              <div className="ruchi-certification-details">
                <div className="ruchi-certification-header">
                  <h4>AWS Data Engineer</h4>
                </div>
                <a href="https://www.credly.com/badges/d10ea2d7-9896-4c7d-ae32-8670f8b318f3/linked_in_profile" target="_blank" rel="noopener noreferrer" className="ruchi-credential-link">
                  <i className="fas fa-external-link-alt"></i> View Credential
                </a>
              </div>
            </div>

            <div className="ruchi-certification-item">
              <div className="ruchi-certification-logo">
                <img src={databricksLogo} alt="Databricks" />
              </div>
              <div className="ruchi-certification-details">
                <div className="ruchi-certification-header">
                  <h4>Databricks Fundamentals</h4>
                </div>
                <a href="https://credentials.databricks.com/a23002ca-7b0b-46db-b094-aa01c5bfd7f3#acc.PTs4MDJk" target="_blank" rel="noopener noreferrer" className="ruchi-credential-link">
                  <i className="fas fa-external-link-alt"></i> View Credential
                </a>
              </div>
            </div>

            <div className="ruchi-certification-item">
              <div className="ruchi-certification-logo">
                <img src={awsCommunityLogo} alt="AWS Community Builder" />
              </div>
              <div className="ruchi-certification-details">
                <div className="ruchi-certification-header">
                  <h4>AWS Community Builder</h4>
                </div>
                <a href="https://aws.amazon.com/developer/community/community-builders/" target="_blank" rel="noopener noreferrer" className="ruchi-credential-link">
                  <i className="fas fa-external-link-alt"></i> Learn More
                </a>
              </div>
            </div>
          </div>
        </Fade>
      </div>
    </section>
    </>
  );
}
